/**
 * Content Approvals grid for Magento write-back proposals.
 *
 * Shows pending proposals with a field diff (previous vs proposed value),
 * and supports per-row approve/reject plus a bulk-approve action.
 *
 * Calls existing /api/magento/writeback/* endpoints
 * (backend-merged/routes/magento.js).
 *
 * PRD: cross-cutting/prds/2026-06-12-I4-magento-adobe-commerce/prd.md §6
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  getWritebackProposals,
  approveWriteback,
  rejectWriteback,
  type MagentoWritebackProposal,
} from '@/services/magentoIntegrationService';

const PAGE_SIZE = 50;

const STATUS_BADGE: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  pending:     { variant: 'secondary', label: 'Pending' },
  applied:     { variant: 'default',   label: 'Applied' },
  failed:      { variant: 'destructive', label: 'Failed' },
  rolled_back: { variant: 'outline',   label: 'Rolled back' },
};

export default function MagentoApprovalsGrid() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const siteId = selectedSite?.id || (selectedSite as any)?._id;

  const [proposals, setProposals] = useState<MagentoWritebackProposal[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [actionInFlight, setActionInFlight] = useState<Set<string>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Tracks the AbortController for the current in-flight load() request so it
  // can be cancelled when the site changes or the component unmounts.
  const loadAbortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    // Cancel any previous in-flight load before starting a new one.
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;

    setLoading(true);
    setError(null);
    const loadedForSiteId = siteId;
    try {
      const data = await getWritebackProposals(loadedForSiteId, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        signal: controller.signal,
      });
      // Guard: discard results if the site changed while the request was in flight.
      if (controller.signal.aborted) return;
      setProposals(data.proposals);
      setTotal(data.total);
    } catch (err: any) {
      if (err.name === 'AbortError') return; // stale request — ignore
      setError(err.message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [siteId, page]);

  // Cancel in-flight load on unmount.
  useEffect(() => () => { loadAbortRef.current?.abort(); }, []);
  // On site change: abort in-flight load, reset all state BEFORE the load effect
  // fires so stale cross-site log IDs cannot be bulk-approved on the wrong tenant.
  // Must be declared before the [load] effect — React runs effects in declaration
  // order; placing reset after load would abort the request load just started.
  useEffect(() => {
    loadAbortRef.current?.abort();
    setProposals([]);
    setTotal(0);
    setSelected(new Set());
    setPage(0);
    setActionInFlight(new Set());
    setBulkApproving(false);
    setLoading(true);
    setError(null);
  }, [siteId]);
  useEffect(() => { load(); }, [load]);
  // Clear selection when page changes
  useEffect(() => { setSelected(new Set()); }, [page]);

  if (!siteId) {
    return (
      <div className="p-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Select a site to view write-back proposals.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const pendingIds = proposals.filter((p) => p.writeback_status === 'pending').map((p) => p.id);
    if (pendingIds.every((id) => selected.has(id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingIds));
    }
  };

  const handleApprove = async (logId: string) => {
    if (!siteId) return;
    setActionInFlight((prev) => new Set(prev).add(logId));
    try {
      await approveWriteback(logId, siteId);
      toast({ title: 'Approved', description: `Write-back ${logId.slice(0, 8)}… applied.` });
      load();
    } catch (err: any) {
      toast({ title: 'Approve failed', description: err.message, variant: 'destructive' });
    } finally {
      setActionInFlight((prev) => { const n = new Set(prev); n.delete(logId); return n; });
    }
  };

  const handleReject = async (logId: string) => {
    if (!siteId) return;
    setActionInFlight((prev) => new Set(prev).add(logId));
    try {
      await rejectWriteback(logId, siteId);
      toast({ title: 'Rejected', description: `Write-back ${logId.slice(0, 8)}… rejected.` });
      load();
    } catch (err: any) {
      toast({ title: 'Reject failed', description: err.message, variant: 'destructive' });
    } finally {
      setActionInFlight((prev) => { const n = new Set(prev); n.delete(logId); return n; });
    }
  };

  const handleBulkApprove = async () => {
    if (!siteId) return;
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkApproving(true);
    let successCount = 0;
    let failCount = 0;
    await Promise.allSettled(
      ids.map(async (id) => {
        try {
          await approveWriteback(id, siteId);
          successCount++;
        } catch {
          failCount++;
        }
      }),
    );
    toast({
      title: 'Bulk approve complete',
      description: `${successCount} applied${failCount > 0 ? `, ${failCount} failed` : ''}.`,
      variant: failCount > 0 ? 'destructive' : 'default',
    });
    setSelected(new Set());
    setBulkApproving(false);
    load();
  };

  const pendingCount = proposals.filter((p) => p.writeback_status === 'pending').length;
  const allPendingSelected =
    pendingCount > 0 &&
    proposals.filter((p) => p.writeback_status === 'pending').every((p) => selected.has(p.id));
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-8 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Content Approvals</h1>
          <p className="text-muted-foreground mt-1">
            Review and approve AEO attribute write-backs to Magento.
          </p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button onClick={handleBulkApprove} disabled={bulkApproving} size="sm">
              {bulkApproving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Approving {selected.size}…</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 mr-2" /> Approve {selected.size} selected</>
              )}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RotateCcw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pending Proposals</CardTitle>
          <CardDescription>{total} pending total</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : proposals.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6 text-center">No proposals found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allPendingSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Select all pending"
                    />
                  </TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Attribute</TableHead>
                  <TableHead>Before</TableHead>
                  <TableHead>After</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Proposed</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {proposals.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      {p.writeback_status === 'pending' && (
                        <Checkbox
                          checked={selected.has(p.id)}
                          onCheckedChange={() => toggleSelect(p.id)}
                          aria-label={`Select ${p.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.product_sku}</TableCell>
                    <TableCell className="font-mono text-xs">{p.attribute_code}</TableCell>
                    <TableCell className="max-w-[160px] truncate text-xs text-muted-foreground">
                      {p.previous_value || <em className="opacity-50">empty</em>}
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate text-xs">
                      {p.proposed_value}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={STATUS_BADGE[p.writeback_status]?.variant ?? 'outline'}
                        className="text-xs"
                      >
                        {STATUS_BADGE[p.writeback_status]?.label ?? p.writeback_status}
                      </Badge>
                      {p.error_message && (
                        <p className="text-xs text-destructive mt-1 truncate max-w-[120px]">
                          {p.error_message}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(p.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {p.writeback_status === 'pending' && (
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-green-600 hover:text-green-700"
                            disabled={actionInFlight.has(p.id)}
                            onClick={() => handleApprove(p.id)}
                            title="Approve"
                          >
                            {actionInFlight.has(p.id) ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            disabled={actionInFlight.has(p.id)}
                            onClick={() => handleReject(p.id)}
                            title="Reject"
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
